import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Button, Input, Modal, Space, Tag, Typography, message } from 'antd'
import React, { useState } from 'react'
import { holdRefund, refundQueries, withdrawRefund } from '../../../../../Actions'
import {
  canHoldRefund,
  canWithdrawRefund,
  InvoiceRefund,
  REFUND_STATUS_LABEL,
  RefundLedgerRow,
} from '../../../../../types/refund'
import { apiErrorMessage } from '../../../../../utils/apiError'
import useInvalidateRefunds from './useInvalidateRefunds'

type Props = {
  row: RefundLedgerRow
  workspaceId: number
  /** 상태가 바뀌면 상세를 닫는다 — 열어둔 채로는 낡은 상태의 확정 폼이 남는다 */
  onDone?: () => void
}

type Action = 'HOLD' | 'WITHDRAW'

const TITLE: Record<Action, string> = { HOLD: '환불 보류', WITHDRAW: '환불 철회' }

/**
 * 서버가 사유를 주지 않았을 때 대신 보여줄 문구.
 *
 * 규칙 자체를 여기 적어 두는 이유는, 400 본문이 비어 올 수 있어서다(`utils/apiError`).
 * "실패했습니다" 만 띄우면 담당자는 무엇을 고쳐야 할지 모른 채 같은 버튼을 다시 누른다.
 */
const FALLBACK: Record<Action, string> = {
  HOLD: '보류로 바꾸지 못했습니다. 접수 상태에서만 보류할 수 있습니다. (서버가 사유를 주지 않았습니다)',
  WITHDRAW:
    '철회하지 못했습니다. 처리 완료된 행은 철회할 수 없습니다 — 정정은 새 기록입니다. (서버가 사유를 주지 않았습니다)',
}

const settled = (status: InvoiceRefund['status']) => status === 'COMPLETED' || status === 'WITHDRAWN'

/**
 * 보류 · 철회 (화면 명세 2.1 · aipark-front #266 P3)
 *
 * 철회는 **재산정의 유일한 경로**다. 접수 행의 산정값은 고객에게 안내·동의받은 금액이라 고칠 수
 * 없고, 기준일이 지나 다시 계산해야 하면 원 행을 철회하고 새로 접수한다.
 */
export default function RefundStateActions({ row, workspaceId, onDone }: Props) {
  const [action, setAction] = useState<Action | null>(null)
  const [memo, setMemo] = useState('')
  const invalidate = useInvalidateRefunds(workspaceId)

  /**
   * 철회할 때만 짝 행을 읽는다. 서버는 **행 단위로만 철회한다** — 같은 사건의 다른 행을
   * 연쇄로 철회해 주지 않는다(`InvoiceRefundService.withdraw`). 되감기 이탈형은 2행이라
   * 한쪽만 철회하면 사건이 반만 취소된 채 남는다.
   */
  const { data: siblings } = useQuery({
    ...refundQueries.group(row.refundGroupNo ?? ''),
    enabled: action === 'WITHDRAW' && !!row.refundGroupNo && row.pairSize > 1,
  })

  const pendingSiblings = (siblings ?? []).filter((s) => s.id !== row.refundId && !settled(s.status))

  const { mutateAsync: hold, isPending: holding } = useMutation({
    mutationFn: (note?: string) => holdRefund(row.refundId, note),
  })
  const { mutateAsync: withdraw, isPending: withdrawing } = useMutation({
    mutationFn: (note?: string) => withdrawRefund(row.refundId, note),
  })

  const close = () => {
    setAction(null)
    setMemo('')
  }

  const run = async () => {
    if (!action) return
    const note = memo.trim() || undefined

    try {
      if (action === 'HOLD') await hold(note)
      else await withdraw(note)
    } catch (e) {
      message.error(apiErrorMessage(e) ?? FALLBACK[action])
      return
    }

    message.success(action === 'HOLD' ? '보류로 바꿨습니다.' : '철회했습니다.')
    await invalidate()
    close()
    onDone?.()
  }

  const holdable = canHoldRefund(row.status)
  const withdrawable = canWithdrawRefund(row.status)

  if (!holdable && !withdrawable) return null

  return (
    <>
      <Space>
        {holdable ? (
          <Button size="small" onClick={() => setAction('HOLD')}>
            보류
          </Button>
        ) : null}
        {withdrawable ? (
          <Button size="small" danger onClick={() => setAction('WITHDRAW')}>
            철회
          </Button>
        ) : null}
      </Space>

      <Modal
        open={!!action}
        title={action ? TITLE[action] : ''}
        okText={action === 'HOLD' ? '보류' : '철회'}
        okButtonProps={{ danger: action === 'WITHDRAW', loading: holding || withdrawing }}
        cancelText="닫기"
        onOk={run}
        onCancel={close}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {action === 'WITHDRAW' ? (
            <Alert
              type="warning"
              showIcon
              message="철회는 되돌릴 수 없습니다"
              description="이 행은 다시 접수 상태로 돌아오지 않습니다. 금액을 다시 산정해야 한다면 철회한 뒤 결제 건 목록에서 새로 접수하세요."
            />
          ) : null}

          {action === 'WITHDRAW' && row.pairSize > 1 ? (
            <Alert
              type="error"
              showIcon
              message="이 행만 철회됩니다"
              description={
                <Space direction="vertical" size={4}>
                  <span>
                    사건 <b>{row.refundGroupNo}</b> 은 {row.pairSize}행입니다. 서버는 짝 행을 함께 철회하지 않으므로
                    나머지도 각각 철회해야 사건이 온전히 취소됩니다.
                  </span>
                  {pendingSiblings.length ? (
                    <span>
                      남는 행:{' '}
                      {pendingSiblings.map((s) => (
                        <Tag key={s.id}>
                          #{s.id} · {REFUND_STATUS_LABEL[s.status] ?? s.status}
                        </Tag>
                      ))}
                    </span>
                  ) : (
                    <Typography.Text type="secondary">
                      나머지 행은 이미 처리 완료·철회 상태이거나 아직 불러오는 중입니다.
                    </Typography.Text>
                  )}
                </Space>
              }
            />
          ) : null}

          <span>메모 (선택)</span>
          <Input.TextArea
            rows={3}
            value={memo}
            maxLength={500}
            showCount
            placeholder={
              action === 'HOLD' ? '무엇을 기다리는지 적어두면 다음 담당자가 압니다' : '철회 사유를 적어두세요'
            }
            onChange={(e) => setMemo(e.target.value)}
          />
          {/* 서버는 메모를 비우면 접수 때 적은 메모를 유지한다. 지우는 경로가 아니다 */}
          <Typography.Text type="secondary">비워두면 접수 때 적은 메모가 그대로 남습니다.</Typography.Text>
        </Space>
      </Modal>
    </>
  )
}
